import { FC, ReactNode, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import fileDownload from 'js-file-download';
import { DateTime } from 'luxon';
import { api } from 'services/api';
import { TPayableInvoice, TTryToPayResult } from 'services/api/payments/types';
import { notificationContainer } from 'utils/notificationContainer';
import { receiptDownloadUrl } from 'utils/receipt';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';

type TPaymentResultProps = {
	result: TTryToPayResult;
	invoice?: TPayableInvoice;
	methodLabel?: string;
	directDebit: boolean;
};

type TView = {
	icon: ReactNode;
	title: string;
	text: string;
};

const view = (result: TTryToPayResult, directDebit: boolean, invoiceNumber: string): TView => {
	switch (result.status) {
		case 'succeeded':
			return {
				icon: <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />,
				title: 'Payment successful',
				text: `Invoice ${invoiceNumber} has been paid. You can download the receipt and the invoice below.`,
			};
		case 'pending':
			return {
				icon: <HourglassTopIcon sx={{ fontSize: 64, color: 'info.main' }} />,
				title: directDebit ? 'Direct Debit payment initiated' : 'Payment initiated',
				text: directDebit
					? 'The payment will be collected by Direct Debit within about 3 working days. The invoice will be marked as paid once the payment is confirmed.'
					: result.message,
			};
		case 'processing':
			return {
				icon: <HourglassTopIcon sx={{ fontSize: 64, color: 'warning.main' }} />,
				title: 'Payment is being processed',
				text: result.message,
			};
		default:
			return {
				icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />,
				title: 'Payment failed',
				text: result.message || 'The payment could not be completed.',
			};
	}
};

const Row: FC<{ label: string; children: ReactNode; strong?: boolean }> = ({
	label,
	children,
	strong,
}) => (
	<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: '6px' }}>
		<Typography sx={{ color: 'text.secondary', fontSize: '.875rem' }}>{label}</Typography>
		<Typography
			component="div"
			sx={{
				fontSize: '.875rem',
				fontWeight: strong ? 700 : 500,
				textAlign: 'right',
				wordBreak: 'break-all',
			}}
		>
			{children}
		</Typography>
	</Box>
);

export const PaymentResult: FC<TPaymentResultProps> = ({
	result,
	invoice,
	methodLabel,
	directDebit,
}) => {
	const [invoiceLoading, setInvoiceLoading] = useState(false);

	const invoiceNumber = result.invoiceNumber ?? invoice?.invoiceNumber;
	const invoiceTitle = invoiceNumber ? `#${invoiceNumber}` : '';
	const invoiceId = result.invoiceId ?? invoice?.invoiceId ?? null;
	const { icon, title, text } = view(result, directDebit, invoiceTitle);
	const charged = result.status === 'succeeded' || result.status === 'pending';
	const completedAt = result.completedAt
		? DateTime.fromISO(result.completedAt).toLocaleString(DateTime.DATETIME_MED)
		: null;
	const label = result.label ?? methodLabel;

	const handleDownloadInvoice = async () => {
		if (!invoiceId || invoiceLoading) return;
		setInvoiceLoading(true);
		try {
			// the API returns response.data (a Blob, responseType: 'blob'), its declared type is the AxiosResponse
			const pdf = (await api.invoices.getDownloadInvoicePdf({ invoiceId })) as unknown as Blob;
			if (pdf) fileDownload(pdf, `invoice_${invoiceNumber ?? invoiceId}.pdf`);
		} catch {
			notificationContainer(
				'The invoice could not be downloaded. Please try again later.',
				'error',
			);
		} finally {
			setInvoiceLoading(false);
		}
	};

	return (
		<Box>
			<Box sx={{ textAlign: 'center', pt: 1, pb: 2 }}>
				{icon}
				<Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
					{title}
				</Typography>
				<Typography sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 440, mx: 'auto' }}>
					{text}
				</Typography>
			</Box>

			<Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1 }}>
				{invoiceTitle && (
					<Row label="Invoice">
						{invoiceTitle}
						{invoice?.service ? ` · ${invoice.service.name}` : ''}
					</Row>
				)}
				{label && <Row label="Payment method">{label}</Row>}
				<Row label="Amount">
					<AmountAndCurrency amount={result.amount} currency={result.currency} />
				</Row>
				<Row label="Bank fee">
					<AmountAndCurrency amount={result.fee} currency={result.currency} />
				</Row>
				<Divider sx={{ my: '4px' }} />
				<Row label={charged ? 'Total charged' : 'Total'} strong>
					<AmountAndCurrency
						amount={charged ? result.chargedAmount : result.total}
						currency={charged ? result.chargedCurrency : result.currency}
						bold
					/>
				</Row>
				{completedAt && <Row label="Date">{completedAt}</Row>}
				<Row label="Transaction ID">
					<Box component="span" sx={{ fontFamily: 'monospace', fontSize: '.8rem' }}>
						{result.transactionId}
					</Box>
				</Row>
			</Paper>

			{(result.receiptUrl || (invoiceId && result.status !== 'failed')) && (
				<Box
					sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 2.5 }}
				>
					{result.receiptUrl && (
						<Button
							variant="outlined"
							startIcon={<ReceiptLongOutlinedIcon />}
							href={receiptDownloadUrl(result.receiptUrl)}
							target="_blank"
							rel="noopener noreferrer"
						>
							Download receipt
						</Button>
					)}
					{invoiceId && result.status !== 'failed' && (
						<Button
							variant="outlined"
							startIcon={
								invoiceLoading ? <CircularProgress size={16} /> : <DescriptionOutlinedIcon />
							}
							onClick={handleDownloadInvoice}
							disabled={invoiceLoading}
						>
							Download invoice
						</Button>
					)}
				</Box>
			)}
		</Box>
	);
};
